import { Cpu, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Service } from "../types/service";
import { iconMap } from "../types/icons";
import dbJson from "../data/db.json";
import { ScrollToTopButton } from "./ui/scrollToTopButton";

export const ServicesPage = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setServices(dbJson.servicesData);
    setLoading(false);
  }, []);

  return (
    <div className="pt-16 md:pt-20 min-h-screen" data-testid="services-page">
      {/* Header */}
      <div className="bg-slate-900 text-white py-20">
        <div className="container-custom">
          <p className="text-[#0EA5E9] font-mono text-sm uppercase tracking-wider mb-4">
            Услуги компании
          </p>
          <h1 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4">
            Полный цикл услуг
          </h1>
          <p className="text-slate-400 max-w-2xl text-lg">
            От разработки до сервисного обслуживания — мы обеспечиваем комплексную поддержку на всех этапах
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-slate-200 animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = iconMap.hasOwnProperty(service.icon)
                  ? iconMap[service.icon as keyof typeof iconMap]
                  : Cpu;
                return (
                  <div
                    key={service.id}
                    className="bg-white p-8 border-l-4 border-transparent hover:border-[#0056D2] transition-all hover:shadow-lg animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`service-card-${service.id}`}
                  >
                    <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center mb-6">
                      <IconComponent size={28} className="text-[#0056D2]" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">Как мы работаем</h2>
            <p className="section-subtitle mx-auto">
              Прозрачный процесс от первого контакта до завершения проекта
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Консультация", desc: "Анализ задачи и подбор решения" },
              { step: "02", title: "Разработка", desc: "Проектирование и создание продукта" },
              { step: "03", title: "Внедрение", desc: "Монтаж, настройка и интеграция" },
              { step: "04", title: "Поддержка", desc: "Сервис и техническое обслуживание" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-slate-200 mb-4">{item.step}</div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0056D2]">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-tight mb-4">
            Нужна консультация?
          </h2>
          <p className="text-blue-100 mb-6">
            Свяжитесь с нами для обсуждения вашего проекта
          </p>
          <Link to="/contacts" className="inline-flex items-center justify-center bg-white text-[#0056D2] font-semibold text-sm uppercase tracking-wide py-3 px-8 rounded-sm hover:bg-slate-100 transition-colors" data-testid="services-contact-btn">
            Контакты
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
      <ScrollToTopButton />
    </div>
  );
};